class NavigationDrawer {
  constructor() {
    this.drawer = document.getElementById('nav-drawer');
    this.openBtn = document.querySelector('.header__burger');
    this.closeBtns = document.querySelectorAll('[data-close]');

    if (!this.drawer || !this.openBtn) return;

    this.bind();
    this.initAccordions();
  }

  bind() {
    this.openBtn.addEventListener('click', () => this.open());

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.drawer.hidden = false;

    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
    });

    this.openBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('nav-lock');
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.openBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('nav-lock');

    setTimeout(() => {
      this.drawer.hidden = true;
    }, 500);
  }

  initAccordions() {
    const items = document.querySelectorAll('[data-accordion]');

    items.forEach(item => {
      const trigger = item.querySelector('.nav-item__trigger');

      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all top-level accordions + their nested sub-accordions
        items.forEach(i => {
          i.classList.remove('is-open');
          i.querySelector('.nav-item__trigger')?.setAttribute(
            'aria-expanded',
            'false'
          );
          i.querySelectorAll('[data-sub-accordion]').forEach(sub => {
            sub.classList.remove('is-open');
            sub
              .querySelector('.nav-item__child--heading')
              ?.setAttribute('aria-expanded', 'false');
          });
        });

        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Sub-accordions (3rd-level groups)
    const subItems = document.querySelectorAll(
      '[data-sub-accordion]'
    );

    subItems.forEach(sub => {
      const trigger = sub.querySelector('.nav-item__child--heading');

      trigger.addEventListener('click', () => {
        const isOpen = sub.classList.contains('is-open');

        // Close sibling sub-accordions within the same parent
        const parent = sub.closest('.nav-item__children');
        parent
          ?.querySelectorAll('[data-sub-accordion]')
          .forEach(s => {
            s.classList.remove('is-open');
            s.querySelector(
              '.nav-item__child--heading'
            )?.setAttribute('aria-expanded', 'false');
          });

        if (!isOpen) {
          sub.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
}

class MegaMenu {
  constructor() {
    this.root = document.querySelector('[data-mega-menu]');
    this.triggers = document.querySelectorAll('[data-mega-trigger]');

    if (!this.root || !this.triggers.length) return;

    this.panels = this.root.querySelectorAll('[data-mega-panel]');
    this.activePanel = null;
    this.activeTrigger = null;

    this.bind();
    this.initPreviews();
    this.schedulePreload();
  }

  // The menu root and its preview panes are hidden, so their lazy images
  // never intersect the viewport and don't fetch until the menu opens.
  // Flip them to eager once the page has loaded and gone idle, so the
  // menu is instant by the time anyone opens it.
  preloadImages() {
    this.root.querySelectorAll('img[loading="lazy"]').forEach(img => {
      img.loading = 'eager';
    });
  }

  schedulePreload() {
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.preloadImages(), { timeout: 3000 });
      } else {
        setTimeout(() => this.preloadImages(), 1500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  bind() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const name = trigger.dataset.megaTrigger;
        if (this.activePanel === name) {
          this.close();
        } else {
          this.open(name, trigger);
        }
      });
    });

    // Backdrop click
    this.root
      .querySelector('[data-mega-close]')
      ?.addEventListener('click', () => this.close());

    // Escape key — return focus to the trigger
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.activePanel) {
        const trigger = this.activeTrigger;
        this.close();
        trigger?.focus();
      }
    });

    // Click outside the header/panel
    document.addEventListener('click', e => {
      if (!this.activePanel) return;
      if (
        e.target.closest('.header') ||
        e.target.closest('[data-mega-panel]')
      )
        return;
      this.close();
    });
  }

  open(name, trigger) {
    this.preloadImages();
    this.root.hidden = false;

    this.panels.forEach(panel => {
      const isTarget = panel.dataset.megaPanel === name;
      panel.hidden = !isTarget;
      requestAnimationFrame(() => {
        panel.classList.toggle('is-open', isTarget);
      });
    });

    requestAnimationFrame(() => {
      this.root.classList.add('is-open');
    });

    this.triggers.forEach(t =>
      t.setAttribute(
        'aria-expanded',
        t === trigger ? 'true' : 'false'
      )
    );

    this.activePanel = name;
    this.activeTrigger = trigger;
    document.body.classList.add('nav-lock');
  }

  close() {
    this.root.classList.remove('is-open');
    this.panels.forEach(panel => panel.classList.remove('is-open'));
    this.triggers.forEach(t =>
      t.setAttribute('aria-expanded', 'false')
    );
    document.body.classList.remove('nav-lock');

    this.activePanel = null;
    this.activeTrigger = null;

    setTimeout(() => {
      if (!this.activePanel) {
        this.root.hidden = true;
        this.panels.forEach(panel => (panel.hidden = true));
      }
    }, 300);
  }

  // Hover/focus a sublink -> swap the preview pane
  initPreviews() {
    this.panels.forEach(panel => {
      const links = panel.querySelectorAll('[data-mega-link]');
      const previews = panel.querySelectorAll('[data-mega-preview]');

      const activate = id => {
        links.forEach(l =>
          l.classList.toggle('is-active', l.dataset.megaLink === id)
        );
        previews.forEach(p => {
          p.hidden = p.dataset.megaPreview !== id;
        });
      };

      links.forEach(link => {
        const id = link.dataset.megaLink;
        link.addEventListener('mouseenter', () => activate(id));
        link.addEventListener('focus', () => activate(id));
      });
    });
  }
}

class HeaderScroll {
  constructor() {
    this.header = document.querySelector('.header');
    this.announcementBar = document.querySelector(
      '.announcement-bar'
    );

    if (!this.header) return;

    this.lastScroll = 0;
    this.ticking = false;

    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });

        this.ticking = true;
      }
    });
  }

  handleScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll <= 0) {
      this.header.classList.remove('header--hidden');
      this.header.classList.remove('header--solid');
      this.announcementBar?.classList.remove(
        'announcement-bar--hidden'
      );
      this.lastScroll = currentScroll;
      return;
    }

    if (currentScroll > this.lastScroll && currentScroll > 150) {
      this.header.classList.add('header--hidden');
      this.header.classList.add('header--solid');
      this.announcementBar?.classList.add('announcement-bar--hidden');
    } else {
      this.header.classList.remove('header--hidden');
      this.announcementBar?.classList.remove(
        'announcement-bar--hidden'
      );
    }

    this.lastScroll = currentScroll;
  }
}

class HeaderTheme {
  constructor() {
    this.header = document.querySelector('.header');
    this.firstSection = document.querySelector(
      'main section:first-child[data-header-theme="light"]'
    );
    if (!this.header || !this.firstSection) return;
    this.update();
    window.addEventListener('scroll', () => this.update(), {
      passive: true
    });
  }

  update() {
    const inView = window.scrollY < this.firstSection.offsetHeight;
    this.header.classList.toggle('header--light', inView);
  }
}

class DrawerProductSwipers {
  constructor() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('[data-drawer-swiper]').forEach(el => {
      new Swiper(el, {
        slidesPerView: 2.5,
        spaceBetween: 12,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20,
        observer: true,
        observeParents: true,
        breakpoints: {
          600: {
            slidesPerView: 3.5,
            spaceBetween: 16,
            slidesOffsetBefore: 20,
            slidesOffsetAfter: 20
          },
          768: {
            slidesPerView: 5,
            spaceBetween: 16,
            slidesOffsetBefore: 20,
            slidesOffsetAfter: 20
          }
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new NavigationDrawer();
  new MegaMenu();
  new HeaderScroll();
  new HeaderTheme();
  new DrawerProductSwipers();
});
